import { z } from 'zod';
/**
 * The fee schedule for tax advisers (StBVV) as data (ADR-071 §4, `specs/KAOS-STBV/STBV-R`): the
 * fee tables, the frames of the provisions and the time fee, one version per wording in force.
 * Amounts are integer cents. The schedule itself ships with `@kanos/rules` (`reference/stbvv`);
 * here is what it means and how a fee follows from it, the same in the browser and on the server.
 */
/** Integer cents, never negative. */
const Cents = z.number().int().nonnegative();
const isoDate = z.iso.date();
export const FeeTableId = z.enum(['A', 'B', 'C']).meta({ id: 'FeeTableId' });
/** A tenth rate such as 4/10 or 3/20 (§ 10 StBVV). */
export const FeeRate = z
    .object({
    numerator: z.number().int().positive(),
    denominator: z.union([z.literal(10), z.literal(20)]),
})
    .meta({ id: 'FeeRate' });
/** One step of a table: the full fee (10/10) for a value up to `upTo`. */
const FeeStep = z.object({ upTo: Cents, fee: Cents });
/** Above the last step: for every started `per` of the excess up to `upTo` (open when absent), `fee`. */
const FeeIncrement = z.object({ upTo: Cents.optional(), per: Cents.positive(), fee: Cents });
export const FeeTable = z
    .object({
    id: FeeTableId,
    title: z.string().min(1),
    source: z.string().min(1),
    steps: z.array(FeeStep).min(1),
    beyond: z.array(FeeIncrement),
})
    .meta({ id: 'FeeTable' });
/** How a provision prices: by value and tenth rate, by a fixed amount range, or by time. */
export const FeeProvision = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('value'),
        id: z.string().min(1),
        norm: z.string().min(1),
        title: z.string().min(1),
        table: FeeTableId,
        min: FeeRate,
        max: FeeRate,
        /** The least value the fee is taken from, when the provision sets one. */
        minValue: Cents.optional(),
        valueBasis: z.string().min(1),
    }),
    z.object({
        kind: z.literal('amount'),
        id: z.string().min(1),
        norm: z.string().min(1),
        title: z.string().min(1),
        min: Cents,
        max: Cents,
        unit: z.string().min(1),
    }),
    z.object({
        kind: z.literal('time'),
        id: z.string().min(1),
        norm: z.string().min(1),
        title: z.string().min(1),
    }),
])
    .meta({ id: 'FeeProvision' });
export const FeeSchedule = z
    .object({
    version: z.string().min(1),
    validFrom: isoDate,
    source: z.string().min(1),
    /** SHA-256 over the schedule as shipped: two schedules with the same digest price the same. */
    digest: z.string().min(1),
    timeFee: z.object({ minutes: z.number().int().positive(), min: Cents, max: Cents }),
    tables: z.array(FeeTable),
    provisions: z.array(FeeProvision),
})
    .meta({ id: 'FeeSchedule' });
/**
 * The version that governs an engagement given on `orderedOn`: the latest one in force by then
 * (§ 41 StBVV - the date of the order counts, not the date of the invoice). Before the first
 * version Kanos knows, there is none - an older wording is not guessed.
 */
export function scheduleOn(schedules, orderedOn) {
    return schedules
        .filter((s) => s.validFrom <= orderedOn)
        .reduce((a, s) => (!a || s.validFrom > a.validFrom ? s : a), undefined);
}
/** The full fee (10/10) of a table for a value in cents. */
export function fullFee(table, value) {
    const step = table.steps.find((s) => value <= s.upTo);
    if (step)
        return step.fee;
    const last = table.steps.at(-1);
    if (!last)
        throw new Error(`Fee table ${table.id} has no steps`);
    let fee = last.fee;
    let from = last.upTo;
    for (const increment of table.beyond) {
        const to = Math.min(value, increment.upTo ?? Number.POSITIVE_INFINITY);
        if (to > from)
            fee += Math.ceil((to - from) / increment.per) * increment.fee;
        if (increment.upTo === undefined || value <= increment.upTo)
            return fee;
        from = increment.upTo;
    }
    throw new Error(`Fee table ${table.id} ends at ${String(from)} cents`);
}
/** The fee at a tenth rate, in cents (rounded half up to the cent). */
export function feeAt(table, value, rate) {
    return Math.round((fullFee(table, value) * rate.numerator) / rate.denominator);
}
/** Where a rate stands against the frame of its provision. */
export const RateInFrame = z.enum(['below', 'within', 'above']).meta({ id: 'RateInFrame' });
function share(rate) {
    return rate.numerator / rate.denominator;
}
/**
 * Below the frame is allowed only as an agreement in text form (§ 4, § 4a StBVV), above it only
 * as an agreement too - either way not a quiet result but a finding for the fee agreement.
 */
export function rateInFrame(provision, rate) {
    if (share(rate) < share(provision.min))
        return 'below';
    if (share(rate) > share(provision.max))
        return 'above';
    return 'within';
}
/** A fee for a value-based provision: the value used (raised to the least value), the fee, the frame. */
export const FeeQuote = z
    .object({
    schedule: z.object({ version: z.string(), validFrom: isoDate, digest: z.string() }),
    provision: z.string(),
    norm: z.string(),
    table: FeeTableId,
    value: Cents,
    fullFee: Cents,
    rate: FeeRate,
    fee: Cents,
    frame: RateInFrame,
})
    .meta({ id: 'FeeQuote' });
export function quoteFee(schedule, provisionId, value, rate) {
    const provision = schedule.provisions.find((p) => p.id === provisionId);
    if (provision?.kind !== 'value') {
        throw new Error(`Provision ${provisionId} is no value-based fee of ${schedule.version}`);
    }
    const table = schedule.tables.find((t) => t.id === provision.table);
    if (!table)
        throw new Error(`Fee table ${provision.table} is missing in ${schedule.version}`);
    const used = Math.max(value, provision.minValue ?? 0);
    return {
        schedule: { version: schedule.version, validFrom: schedule.validFrom, digest: schedule.digest },
        provision: provision.id,
        norm: provision.norm,
        table: table.id,
        value: used,
        fullFee: fullFee(table, used),
        rate,
        fee: feeAt(table, used, rate),
        frame: rateInFrame(provision, rate),
    };
}
/** What `quoteFee` takes over the API: which provision, which value, which rate, which order date. */
export const FeeQuoteQuery = z
    .object({
    provision: z.string().min(1).max(40),
    value: z.coerce.number().int().nonnegative().max(1e14),
    numerator: z.coerce.number().int().positive().max(400),
    denominator: z.coerce.number().pipe(z.union([z.literal(10), z.literal(20)])),
    orderedOn: isoDate.optional(),
})
    .meta({ id: 'FeeQuoteQuery' });
export const FeeScheduleQuery = z
    .object({ orderedOn: isoDate.optional() })
    .meta({ id: 'FeeScheduleQuery' });
//# sourceMappingURL=fee-schedule.js.map